/**
 * VEDANGJYOTISHAM — SESSION & STORAGE PURGE UTILITY
 * Classification: Security Infrastructure (Stage 2 Identity & Session Isolation)
 * 
 * Ensures complete zero-trust data isolation across account boundaries and shared devices.
 * Purges all user-scoped records, active charts, family profiles, BTR events,
 * and consultation stores while preserving non-sensitive user preferences.
 */

// Explicit whitelist of non-sensitive preference keys permitted to survive logout
export const PRESERVED_PREFERENCE_KEYS = new Set([
  'astro_ayanamsha_pref',
  'astro_node_pref',
  'kundali_lang',
  'astro_view_mode',
  'menu_layout_mode',
  'time_format_pref',
  'astro_inspector_default_view',
  'astro_table_hover_tooltips_enabled',
  'astro_theme_preference',
  'astro_chart_style',
  'astro_ai_model',
  'astro_ai_temp',
  'astro_degrees_pref',
  'astro_anti_overlap_pref',
  'astro_show_ask_acharya',
  'astro_chart_layout_mode',
  'astro_sidebar_collapsed',
  'astro_workspace_split_percent',
  'astro_user_location_pref',
  'astro_app_version_snapshots'
]);

/**
 * Purges all identity, private horoscope, and cached state data from browser storage.
 */
export function purgeUserSessionData(): void {
  const storage = typeof window !== 'undefined' && window.localStorage
    ? window.localStorage
    : (typeof localStorage !== 'undefined' ? localStorage : null);

  if (!storage) return;

  try {
    const keysToRemove: string[] = [];
    const storageLen = storage.length;

    for (let i = 0; i < storageLen; i++) {
      const key = storage.key(i);
      if (!key) continue;

      // Strict Allowlist: Non-sensitive preferences, UI flags, and Navtara display settings survive logout
      if (
        PRESERVED_PREFERENCE_KEYS.has(key) ||
        key.startsWith('alert_ack_') ||
        key.startsWith('astro_navtara_')
      ) {
        continue;
      }

      // Zero-Trust Allowlist Invariant: Purge all session, profile, chart, case notes,
      // milestones, bookmarks, custom remedies, and private consultation data across account boundaries
      keysToRemove.push(key);
    }

    keysToRemove.forEach((k) => storage.removeItem(k));
  } catch (e) {
    console.error('[SECURITY] Failed executing session storage purge:', e);
  }

  // Also purge transient sessionStorage across logout
  try {
    const sessStorage = typeof window !== 'undefined' && window.sessionStorage
      ? window.sessionStorage
      : (typeof sessionStorage !== 'undefined' ? sessionStorage : null);
    if (sessStorage) {
      sessStorage.clear();
    }
  } catch (e) {
    console.error('[SECURITY] Failed clearing sessionStorage:', e);
  }
}
